import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  useTransferDestinations,
  useCreateTransferDestination,
  useUpdateTransferDestination,
  useDeleteTransferDestination,
  TransferDestination,
} from "@/hooks/useTransferDestinations";
import { generateSlug } from "@/lib/utils";

const AdminTransferDestinations = () => {
  const { data: destinations, isLoading } = useTransferDestinations();
  const createDestination = useCreateTransferDestination();
  const updateDestination = useUpdateTransferDestination();
  const deleteDestination = useDeleteTransferDestination();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState<TransferDestination | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    title: "",
    description: "",
    hero_image: "",
    content: "",
    highlights: [] as string[],
    popular_routes: [] as Array<{ name: string; time: string; price: number }>,
    gallery_images: [] as string[],
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    is_active: true,
    display_order: 0,
    from_airport_price: null as number | null,
    to_airport_price: null as number | null,
    estimated_time: "",
  });

  const [highlightInput, setHighlightInput] = useState("");
  const [galleryInput, setGalleryInput] = useState("");
  const [routeInput, setRouteInput] = useState({ name: "", time: "", price: 0 });

  const handleOpenDialog = (destination?: TransferDestination) => {
    if (destination) {
      setEditingDestination(destination);
      setFormData({
        name: destination.name,
        slug: destination.slug,
        title: destination.title,
        description: destination.description || "",
        hero_image: destination.hero_image || "",
        content: destination.content || "",
        highlights: destination.highlights || [],
        popular_routes: destination.popular_routes || [],
        gallery_images: destination.gallery_images || [],
        meta_title: destination.meta_title || "",
        meta_description: destination.meta_description || "",
        meta_keywords: destination.meta_keywords || "",
        is_active: destination.is_active,
        display_order: destination.display_order,
        from_airport_price: destination.from_airport_price,
        to_airport_price: destination.to_airport_price,
        estimated_time: destination.estimated_time || "",
      });
    } else {
      setEditingDestination(null);
      setFormData({
        name: "",
        slug: "",
        title: "",
        description: "",
        hero_image: "",
        content: "",
        highlights: [],
        popular_routes: [],
        gallery_images: [],
        meta_title: "",
        meta_description: "",
        meta_keywords: "",
        is_active: true,
        display_order: 0,
        from_airport_price: null,
        to_airport_price: null,
        estimated_time: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleNameChange = (name: string) => {
    setFormData({ ...formData, name, slug: generateSlug(name) });
  };

  const handleAddHighlight = () => {
    if (highlightInput.trim()) {
      setFormData({
        ...formData,
        highlights: [...formData.highlights, highlightInput.trim()],
      });
      setHighlightInput("");
    }
  };

  const handleRemoveHighlight = (index: number) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter((_, i) => i !== index),
    });
  };

  const handleAddGalleryImage = () => {
    if (galleryInput.trim()) {
      setFormData({
        ...formData,
        gallery_images: [...formData.gallery_images, galleryInput.trim()],
      });
      setGalleryInput("");
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setFormData({
      ...formData,
      gallery_images: formData.gallery_images.filter((_, i) => i !== index),
    });
  };

  const handleAddRoute = () => {
    if (routeInput.name && routeInput.time && routeInput.price > 0) {
      setFormData({
        ...formData,
        popular_routes: [...formData.popular_routes, routeInput],
      });
      setRouteInput({ name: "", time: "", price: 0 });
    }
  };

  const handleRemoveRoute = (index: number) => {
    setFormData({
      ...formData,
      popular_routes: formData.popular_routes.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = () => {
    if (editingDestination) {
      updateDestination.mutate(
        { id: editingDestination.id, updates: formData },
        { onSuccess: () => setIsDialogOpen(false) }
      );
    } else {
      createDestination.mutate(formData as any, {
        onSuccess: () => setIsDialogOpen(false),
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteDestination.mutate(id);
  };

  if (isLoading) {
    return <div className="p-8">Cargando destinos...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Destinos de Transfers</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona las landing pages de destinos de transfers
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Destino
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDestination ? "Editar Destino" : "Nuevo Destino"}
              </DialogTitle>
              <DialogDescription>
                Completa la información del destino de transfer
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="basic">Básico</TabsTrigger>
                <TabsTrigger value="content">Contenido</TabsTrigger>
                <TabsTrigger value="gallery">Galería</TabsTrigger>
                <TabsTrigger value="pricing">Precios</TabsTrigger>
                <TabsTrigger value="seo">SEO</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Nombre del Destino *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Ej: Punta Cana"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL) *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="punta-cana"
                  />
                </div>
                <div>
                  <Label htmlFor="title">Título de la Página *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Transfers a Punta Cana - Servicio Premium"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descripción Corta</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción breve del destino"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="display_order">Orden de Visualización</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({ ...formData, display_order: parseInt(e.target.value) })
                    }
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Activo</Label>
                </div>
              </TabsContent>

              <TabsContent value="content" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="hero_image">URL Imagen Hero</Label>
                  <Input
                    id="hero_image"
                    value={formData.hero_image}
                    onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenido Principal</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Contenido detallado del destino..."
                    rows={6}
                  />
                </div>
                <div>
                  <Label>Puntos Destacados</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={highlightInput}
                      onChange={(e) => setHighlightInput(e.target.value)}
                      placeholder="Ej: Servicio 24/7"
                      onKeyPress={(e) => e.key === "Enter" && handleAddHighlight()}
                    />
                    <Button type="button" onClick={handleAddHighlight}>
                      Agregar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                        <span>{highlight}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveHighlight(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Rutas Populares</Label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <Input
                      placeholder="Nombre ruta"
                      value={routeInput.name}
                      onChange={(e) => setRouteInput({ ...routeInput, name: e.target.value })}
                    />
                    <Input
                      placeholder="Tiempo (45 min)"
                      value={routeInput.time}
                      onChange={(e) => setRouteInput({ ...routeInput, time: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Precio"
                        value={routeInput.price || ""}
                        onChange={(e) =>
                          setRouteInput({ ...routeInput, price: parseFloat(e.target.value) })
                        }
                      />
                      <Button type="button" onClick={handleAddRoute}>
                        +
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {formData.popular_routes.map((route, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                        <span>
                          {route.name} - {route.time} - ${route.price}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRoute(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="space-y-4 mt-4">
                <div>
                  <Label>Galería de Imágenes</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={galleryInput}
                      onChange={(e) => setGalleryInput(e.target.value)}
                      placeholder="URL de la imagen"
                      onKeyPress={(e) => e.key === "Enter" && handleAddGalleryImage()}
                    />
                    <Button type="button" onClick={handleAddGalleryImage}>
                      Agregar
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {formData.gallery_images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-32 object-cover rounded"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                          onClick={() => handleRemoveGalleryImage(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="from_airport_price">Precio desde Aeropuerto (USD)</Label>
                  <Input
                    id="from_airport_price"
                    type="number"
                    value={formData.from_airport_price || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        from_airport_price: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    placeholder="75"
                  />
                </div>
                <div>
                  <Label htmlFor="to_airport_price">Precio hacia Aeropuerto (USD)</Label>
                  <Input
                    id="to_airport_price"
                    type="number"
                    value={formData.to_airport_price || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        to_airport_price: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    placeholder="75"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated_time">Tiempo Estimado</Label>
                  <Input
                    id="estimated_time"
                    value={formData.estimated_time}
                    onChange={(e) => setFormData({ ...formData, estimated_time: e.target.value })}
                    placeholder="45 minutos"
                  />
                </div>
              </TabsContent>

              <TabsContent value="seo" className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="meta_title">Meta Título</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Transfers a Punta Cana | Servicio Premium 24/7"
                  />
                </div>
                <div>
                  <Label htmlFor="meta_description">Meta Descripción</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) =>
                      setFormData({ ...formData, meta_description: e.target.value })
                    }
                    placeholder="Descripción para motores de búsqueda"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="meta_keywords">Meta Keywords</Label>
                  <Input
                    id="meta_keywords"
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    placeholder="transfer punta cana, transporte aeropuerto, ..."
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>
                {editingDestination ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Orden</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {destinations?.map((destination) => (
              <TableRow key={destination.id}>
                <TableCell className="font-medium">{destination.name}</TableCell>
                <TableCell>
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    /traslados/{destination.slug}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge variant={destination.is_active ? "default" : "secondary"}>
                    {destination.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </TableCell>
                <TableCell>{destination.display_order}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/traslados/${destination.slug}`, "_blank")}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(destination)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. El destino será eliminado
                            permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(destination.id)}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminTransferDestinations;